import React from "react";
import { DOC_CENTER } from "@/constants/testIds";
import { Inbox, AlertTriangle, Search as SearchIcon, FolderSearch, FileWarning, Mail, FolderX } from "lucide-react";

const ISSUES = [
  { label: "Missing versions", icon: FileWarning, x: "left-[6%]", y: "top-[8%]", r: "-rotate-6" },
  { label: "Searching inboxes", icon: Inbox, x: "left-[28%]", y: "top-[58%]", r: "rotate-3" },
  { label: "Wrong attachments", icon: AlertTriangle, x: "left-[44%]", y: "top-[14%]", r: "rotate-2" },
  { label: "Document confusion", icon: FolderX, x: "left-[64%]", y: "top-[62%]", r: "-rotate-3" },
  { label: "Supplier emails", icon: Mail, x: "left-[12%]", y: "top-[40%]", r: "rotate-1" },
  { label: "Scattered folders", icon: FolderSearch, x: "left-[78%]", y: "top-[18%]", r: "-rotate-2" },
  { label: "Manual searching", icon: SearchIcon, x: "left-[54%]", y: "top-[40%]", r: "rotate-4" },
];

export default function Problem() {
  return (
    <section
      data-testid={DOC_CENTER.problem.section}
      className="relative mt-32 overflow-hidden bg-[#0A0A0A] text-white"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px),linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div className="dc-container relative py-24 md:py-32">
        <div className="max-w-3xl">
          <span className="dc-eyebrow !text-neutral-400" style={{}}>The problem</span>
          <h2 className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tighter text-white sm:text-5xl md:text-6xl">
            Import Documents Shouldn’t<br />
            Live Inside Emails.
          </h2>
          <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-neutral-400">
            The truth of import operations: critical paperwork is fragmented across inboxes, drives,
            phones, suppliers and freight forwarders. Every minute spent searching is a minute the
            shipment is exposed.
          </p>
        </div>

        {/* Chaotic vs clean visual */}
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Chaotic */}
          <div className="relative h-[360px] overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(ellipse_at_top,rgba(239,68,68,0.18),transparent_60%)]">
            <div className="absolute left-4 top-4 font-mono text-[10.5px] uppercase tracking-wider text-rose-300/80">Today · without document center</div>
            {ISSUES.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.label}
                  className={`absolute ${it.x} ${it.y} ${it.r} flex items-center gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/10 px-2.5 py-1.5 backdrop-blur-sm`}
                >
                  <Icon className="h-3.5 w-3.5 text-rose-300" strokeWidth={1.75} />
                  <span className="text-[12px] font-medium text-rose-100">{it.label}</span>
                </div>
              );
            })}
          </div>

          {/* Clean */}
          <div className="relative min-h-[360px] overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
            <div className="absolute left-4 top-4 font-mono text-[10.5px] uppercase tracking-wider text-emerald-300/80">After · one workspace</div>
            <div className="absolute inset-x-0 bottom-0 top-14 overflow-y-auto px-6 pb-4">
              <div className="flex flex-col gap-2">
              {[
                "Purchase Order · v3.2 · Approved",
                "Commercial Invoice · v2.1 · Approved",
                "Packing List · v1.4 · Approved",
                "Bill of Lading · v1.0 · Approved",
                "Certificate of Origin · v1.1 · Approved",
                "Inspection Report · v1.0 · Approved",
                "Freight Invoice · v1.0 · Pending",
              ].map((row, i) => (
                <div
                  key={row}
                  className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-3 py-2 dc-rise"
                  style={{ animationDelay: `${100 + i * 50}ms` }}
                >
                  <span className="font-mono text-[11.5px] tracking-wider text-neutral-300">{row}</span>
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
